/**
 * Адреса раздела «Оплаты» — с 21.10.2026 в нём и прежняя «Сверка» (D267):
 *
 * - `/payments` — платёжки недели;
 * - `/payments/suppliers` — поставщики: долг в системе, по банку и по акту;
 * - `/payments/suppliers/45?period=month|quarter|year&only=issues` — карточка
 *   поставщика: приходы, оплаты и акты одной лентой (ссылкой делятся,
 *   поэтому фильтры — в адресе);
 * - `/payments/acts/701` — акт сверки со строками и парами;
 * - `/payments/statements` — загрузка выписки банка и что в ней;
 * - `/payments/debts?list=overdue|unposted|supplier|search&supplier_id=&q=` — долги;
 * - `/payments/invoices/123` — карточка накладной (ссылка из бота ведёт сюда);
 * - `/payments/reports?code=P4&from=&to=&supplier_id=&min_total=` — отчёты.
 *
 * Прежние адреса `/reconcile/...` переводятся сюда — `fromReconcilePath`.
 */

import type { Navigation } from '../../api';

export type PaymentsTab = 'week' | 'suppliers' | 'statements' | 'debts' | 'reports';

/**
 * Право каждой вкладки (D235): у каждой своя галочка, сервер проверяет её
 * сам. «Поставщики» и «Выписка банка» — право сверки (D261): это деньги, и
 * выдаётся оно поимённо.
 */
const TAB_RIGHTS: Record<PaymentsTab, string> = {
  week: 'payments_week',
  suppliers: 'reconcile',
  statements: 'reconcile',
  debts: 'payments_debts',
  reports: 'payments_reports',
};

const ORDER: PaymentsTab[] = ['week', 'suppliers', 'statements', 'debts', 'reports'];

/** Вкладки «Оплат», открытые человеку, в порядке экрана. */
export function paymentTabs(nav: Navigation): PaymentsTab[] {
  const rights = nav.services.ops?.rights ?? {};
  return ORDER.filter((tab) => nav.user.is_host || Boolean(rights[TAB_RIGHTS[tab]]));
}

export type PaymentsRoute =
  | { tab: PaymentsTab; screen: 'tab' }
  | { tab: 'debts'; screen: 'invoice'; invoiceId: number }
  | { tab: 'suppliers'; screen: 'supplier'; supplierId: number }
  | { tab: 'suppliers'; screen: 'act'; actId: number };

export function readPayments(path: string): PaymentsRoute {
  const card = /^\/payments\/invoices\/(\d+)$/.exec(path);
  if (card) return { tab: 'debts', screen: 'invoice', invoiceId: Number(card[1]) };
  const supplier = /^\/payments\/suppliers\/(\d+)$/.exec(path);
  if (supplier) return { tab: 'suppliers', screen: 'supplier', supplierId: Number(supplier[1]) };
  const act = /^\/payments\/acts\/(\d+)$/.exec(path);
  if (act) return { tab: 'suppliers', screen: 'act', actId: Number(act[1]) };
  if (path.startsWith('/payments/suppliers')) return { tab: 'suppliers', screen: 'tab' };
  if (path.startsWith('/payments/statements')) return { tab: 'statements', screen: 'tab' };
  if (path.startsWith('/payments/debts')) return { tab: 'debts', screen: 'tab' };
  if (path.startsWith('/payments/reports')) return { tab: 'reports', screen: 'tab' };
  return { tab: 'week', screen: 'tab' };
}

/**
 * Прежний адрес «Сверки» — тот же экран в «Оплатах». Ссылки на старые
 * адреса остались в переписке и закладках; открыться они обязаны.
 */
export function fromReconcilePath(path: string): string {
  const rest = path.replace(/^\/reconcile/, '');
  if (rest === '' || rest === '/') return TAB_PATHS.suppliers;
  if (rest.startsWith('/statements')) return TAB_PATHS.statements;
  return `/payments${rest}`;
}

export const TAB_PATHS: Record<PaymentsTab, string> = {
  week: '/payments',
  suppliers: '/payments/suppliers',
  statements: '/payments/statements',
  debts: '/payments/debts',
  reports: '/payments/reports',
};

export function cardPath(id: number): string {
  return `/payments/invoices/${id}`;
}
